/**
 * Rich Design System
 *
 * All design constants (colors, font sizes, chart options) are centralized here.
 * Import from this file instead of hardcoding values.
 */

// ============================================================
// 1. Brand Colors
// ============================================================

export const brand = {
  primary: "#6366F1", // Indigo - app primary
  primaryLight: "#818CF8",
  primaryDark: "#4338CA",
};

// ============================================================
// 2. Chart Color Palette (replaces Highcharts defaults)
// ============================================================

// 20-color palette designed for data visualization
// 최소 17개 시도를 동시에 구분할 수 있도록 색상 간 거리를 최대화
export const chartColors = [
  "#2563EB", // Blue
  "#DC2626", // Red
  "#16A34A", // Green
  "#F59E0B", // Amber
  "#7C3AED", // Violet
  "#0891B2", // Cyan
  "#EA580C", // Orange
  "#BE185D", // Magenta
  "#059669", // Emerald
  "#6B7280", // Gray
  "#A855F7", // Purple-light
  "#0284C7", // Sky-dark
  "#B45309", // Brown
  "#E11D48", // Rose-red
  "#15803D", // Green-dark
  "#D946EF", // Fuchsia
  "#1D4ED8", // Blue-dark
  "#CA8A04", // Yellow-dark
  "#0F766E", // Teal-dark
  "#9F1239", // Wine
];

// ============================================================
// 3. Semantic Colors
// ============================================================

// Heatmap gradient: blue(decrease) → white(neutral) → red(increase)
export const heatmap = {
  decrease: "#3060cf",
  neutral: "#ffffff",
  increase: "#c4463a",
  midWithoutWhite: "#7a5683",
};

// Standard gradient stops (blue → white → red)
export const normalStops = [
  [0, heatmap.decrease],
  [0.5, heatmap.neutral],
  [1, heatmap.increase],
];

export const invertedStops = [
  [0, heatmap.increase],
  [0.5, heatmap.neutral],
  [1, heatmap.decrease],
];

export const normalStopsWithoutWhite = [
  [0, heatmap.decrease],
  [0.5, heatmap.midWithoutWhite],
  [1, heatmap.increase],
];

export const invertedStopsWithoutWhite = [
  [0, heatmap.increase],
  [0.5, heatmap.midWithoutWhite],
  [1, heatmap.decrease],
];

// Risk heatmap: 5-stop gradient for risk monitor
export const riskHeatmapStops = [
  [0, "#1565c0"],
  [0.3, "#64b5f6"],
  [0.5, "#ffffff"],
  [0.7, "#ef9a9a"],
  [1, "#c62828"],
];

// Risk level indicators
export const riskLevel = {
  high: "#d32f2f",
  caution: "#ed6c02",
  normal: "#757575",
  safe: "#2e7d32",
};

// Cycle phase colors
export const cyclePhase = {
  recovery: "#4caf50", // green - index↑ volume↑
  expansion: "#f44336", // red - index↑ volume↓
  slowdown: "#ff9800", // orange - index↓ volume↓
  contraction: "#2196f3", // blue - index↓ volume↑
};

// Transaction type colors
export const dealType = {
  deal: "rgba(22, 69, 240, 1)", // blue
  lease: "rgba(252, 53, 18, 1)", // red
  rent: "rgba(18, 252, 53, 1)", // green
};

// Region compare colors (max 3 regions)
export const compareColors = ["#6366F1", "#F43F5E", "#10B981"];

// ============================================================
// 4. Region Colors (17 sido)
// ============================================================

export const regionColors = new Map([
  ["서울시", "#4338CA"],
  ["경기도", "#0EA5E9"],
  ["인천시", "#8B5CF6"],
  ["세종시", "#059669"],
  ["대전시", "#D97706"],
  ["부산시", "#7C3AED"],
  ["대구시", "#0284C7"],
  ["울산시", "#C026D3"],
  ["전남광주시", "#E11D48"],
  ["강원도", "#16A34A"],
  ["충청북도", "#6D28D9"],
  ["충청남도", "#0891B2"],
  ["경상북도", "#B45309"],
  ["경상남도", "#2563EB"],
  ["전라북도", "#BE185D"],
  ["제주도", "#0F766E"],
]);

// ============================================================
// 5. UI Colors
// ============================================================

export const ui = {
  selectedRow: "#fff7cc",
  selectedItem: "#e3f2fd",
  dragOverBorder: "#6366F1",
  border: "#e0e0e0",
  backgroundGray: "#f5f5f5",
  highlight: "#87CEEB",
  highlightYellow: "#ffff00",
  annotationFill: "rgba(182, 188, 194, 0.3)",
  annotationStroke: "rgba(182, 188, 194, 1)",
  annotationText: "rgba(50, 50, 50, 1)",
  selectedColor: "rgb(249, 84, 38)",
};

// ============================================================
// 6. Chart Defaults
// ============================================================

export const chart = {
  // Title
  titleFontSize: 14,

  // Legend
  legendActiveColor: "#3E576F",
  legendInactiveColor: "#CCC",
  legendFontSize: "12px",
  legendFontColor: "#333",
  legendBackground: "rgba(255,255,255,0.25)",

  // Grid
  gridLineColor: "#e6e6e6",

  // Data labels
  dataLabelColor: "#000000",

  // Line
  defaultLineWidth: 2,

  // Marker
  defaultMarkerRadius: 2,
  hoverLineColor: "#000000",

  // Plot line
  plotLineColor: "black",
  plotLineWidth: 1,
};

// ============================================================
// 7. Font Sizes (semantic tokens)
// ============================================================

export const fontSize = {
  // Chart & data display
  chartTitle: 14,
  chartLabel: 12,
  chartLegend: 12,
  chartDataLabel: 11,
  chartAxisLabel: 12,

  // Compact UI elements
  treeNode: 11,
  treeNodeSub: 8,
  miniLabel: 9,
  smallLabel: 10,
  compactText: 11,
  filterLabel: 11,
  chipLabel: 13,

  // Standard UI
  input: 14,
  tableHead: 12,
  avatar: 14,
  tab: 14,
};

// ============================================================
// 8. Spacing & Sizing
// ============================================================

export const sizing = {
  borderRadius: {
    small: 4,
    default: 8,
    medium: 12,
    large: 16,
    card: 20,
  },
  aptNewWindowSize: 880,
  defaultChartHeight: 650,
};

// Compact analysis selectors: center text/chips and the arrow within one height.
export const analysisSelectSx = {
  height: 28,
  fontSize: fontSize.compactText,
  fontWeight: 700,
  color: chartColors[0],
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
    height: "100%",
    minHeight: 0,
    py: 0,
    lineHeight: 1,
  },
  "& .MuiSelect-icon": {
    fontSize: 20,
    top: "calc(50% - 10px)",
  },
};
